import React, { useMemo, useState } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Sheet from '@amzn/meridian/sheet';
import closeTokens from '@amzn/meridian-tokens/base/icon/close-small';
import { useAuth } from '../auth/AuthContext';
import AppShell, { AppTab } from '../components/AppShell';
import { areasFor } from '../services/types';
import type { AppArea } from '../services/types';
import IntegrationGuide from './IntegrationGuide';
import AsnScreen from './AsnScreen';
import PromisedDateScreen from './PromisedDateScreen';
import LeadTimeScreen from './LeadTimeScreen';
import SupplierControlScreen from './SupplierControlScreen';
import SupplierInsightsScreen from './SupplierInsightsScreen';

export default function LandingScreen() {
    const { user, signOut } = useAuth();
    const areas = useMemo(() => areasFor(user?.roles), [user?.roles]);

    const [area, setArea] = useState<AppArea>(areas[0]);
    const activeArea = areas.includes(area) ? area : areas[0];

    // Connect sub-navigation (masthead tab row) + integration guide.
    const [tab, setTab] = useState<AppTab>('asn');
    const [guideOpen, setGuideOpen] = useState(false);

    // Connect area: masthead tab row + integration guide. Area switching moves
    // to the hamburger/left-nav drawer owned by AppShell.
    if (activeArea === 'connect') {
        const navActions = (
            <Button type="tertiary" size="small" onClick={() => setGuideOpen(true)}>
                Integration guide
            </Button>
        );
        return (
            <AppShell
                userEmail={user?.email}
                onSignOut={signOut}
                navActions={navActions}
                tab={tab}
                onTabChange={setTab}
                areas={areas}
                activeArea={activeArea}
                onAreaChange={setArea}
            >
                <Column spacing="400" width="100%">
                    {tab === 'asn' ? <AsnScreen /> : null}
                    {tab === 'promised' ? <PromisedDateScreen /> : null}
                    {tab === 'leadtime' ? <LeadTimeScreen /> : null}
                </Column>

                <Sheet
                    type="overlay"
                    side="end"
                    open={guideOpen}
                    onClose={() => setGuideOpen(false)}
                >
                    <Column
                        spacing="400"
                        spacingInset="500"
                        width="100%"
                        minWidth={360}
                        maxWidth={640}
                    >
                        <Row alignmentHorizontal="justify" alignmentVertical="center">
                            <Text type="h400">Integration guide</Text>
                            <Button
                                type="icon"
                                label="Close integration guide"
                                onClick={() => setGuideOpen(false)}
                            >
                                <Icon tokens={closeTokens}>Close</Icon>
                            </Button>
                        </Row>
                        <IntegrationGuide />
                    </Column>
                </Sheet>
            </AppShell>
        );
    }

    // Internal areas: no tab row; area switching via the hamburger/left nav.
    return (
        <AppShell
            userEmail={user?.email}
            onSignOut={signOut}
            areas={areas}
            activeArea={activeArea}
            onAreaChange={setArea}
        >
            {activeArea === 'control' ? <SupplierControlScreen /> : <SupplierInsightsScreen />}
        </AppShell>
    );
}
